import { resolveMediaStillUrl, resolveTagStillUrl } from "./tagStill";

/**
 * SKE-4617. Where an Instagram tag is stored, and how the two storage shapes
 * are kept from overlapping.
 *
 * The publish path treats per-slide and post-level tags as ALTERNATIVES rather
 * than additions. `createContainer`
 * (`apps/schedugram-server/src/app/services/instagram-publishing/client.ts:372`)
 * asks `request.media.some(hasItemTags)` once for the whole post: if any slide
 * carries tags of its own, the post-level arrays are not sent at all, on any
 * slide. A single post is built from the post-level arrays only. A story set
 * (SKE-4714) reads each story's own user tags when any story carries some, and
 * otherwise sends the post-level tags on every story.
 *
 * So exactly one of the two shapes may hold tags at a time, and which one is
 * correct depends on whether the post is a carousel. `reconcileTagStorage`
 * moves the tags to the shape the publish path will read, in both directions,
 * and the tagger calls it as it opens so the customer sees the tags that will
 * actually publish.
 */

export type TagStorageMediaFile = {
  url?: string | null;
  originalUrl?: string | null;
  thumbnailUrl?: string | null;
  isVideo?: boolean;
  fileName?: string | null;
  meta?: { mime?: string } | null;
  tags?: unknown[];
  productTags?: unknown[];
};

export type TagStoragePost = {
  mediaFiles?: TagStorageMediaFile[] | null;
  isStory?: boolean;
  tags?: unknown[];
  productTags?: unknown[];
  videoCoverPreview?: string | null;
  thumbnailUrl?: string | null;
  url?: string | null;
  originalUrl?: string | null;
  isVideo?: boolean;
  meta?: { mime?: string } | null;
};

/** The two tag arrays, which are stored and migrated together. */
export const TAG_ARRAY_KEYS = ["tags", "productTags"] as const;
export type TagArrayKey = (typeof TAG_ARRAY_KEYS)[number];

export type TagSlide = {
  index: number;
  /** The image the tags are positioned against. Null while a video is still processing. */
  stillUrl: string | null;
  isVideo: boolean;
  label: string;
};

const asArray = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [];

const mediaFilesOf = (post: TagStoragePost | null | undefined) =>
  Array.isArray(post?.mediaFiles) ? post.mediaFiles : [];

/**
 * SKE-4713. A tag counts only when it names someone or something. A pin saved
 * with an empty username is not a tag, and the publish path never sends one.
 */
const isNamedTag = (tag: unknown): boolean => {
  const value = (tag as { value?: unknown } | null | undefined)?.value;
  return typeof value === "string" && value.trim().length > 0;
};

const namedTagCount = (value: unknown): number =>
  asArray(value).filter(isNamedTag).length;

/** A story with several media: N separate publishes from one post. */
const isStorySet = (post: TagStoragePost | null | undefined) =>
  Boolean(post?.isStory) && mediaFilesOf(post).length > 1;

/**
 * Whether this post's media carry their own tags: a carousel, whose children
 * are tagged slide by slide, or (SKE-4714) a story set, whose stories are each
 * tagged on their own.
 *
 * Mirrors `resolvePublishTarget`
 * (`apps/schedugram-server/src/app/services/instagram-publishing/request.ts:81`).
 */
export function isPerSlideTagPost(
  post: TagStoragePost | null | undefined,
  /**
   * SKE-4733. Per-slide tags exist only for a workspace on the Instagram API
   * track. With it off every post is tagged at the post level, as on the phone
   * path, which publishes one set of tags per post.
   */
  perSlideTagging: boolean = true,
  /**
   * SKE-4714. Per-story tags on a story set. Only true when every selected
   * Instagram account publishes through the API: the phone app places no tags
   * on a story, and a mixed selection keeps the one post-level tagger.
   */
  perStoryTagging: boolean = false
): boolean {
  if (!perSlideTagging || mediaFilesOf(post).length < 2) return false;
  return post?.isStory ? perStoryTagging : true;
}

/** Whether any slide carries tags of its own, matching the server's `hasItemTags`. */
export function hasPerSlideTags(
  post: TagStoragePost | null | undefined
): boolean {
  return mediaFilesOf(post).some((file) =>
    TAG_ARRAY_KEYS.some((key) => asArray(file?.[key]).length > 0)
  );
}

/**
 * SKE-4714. Whether any story of a set carries a NAMED user tag of its own,
 * matching the server adapter's `storySetUserTags`, which decides per-story
 * publishing from mapped user tags alone. A blank pin or a product tag on a
 * story does not stop the post-level tags riding every story.
 */
export function hasPerStoryUserTags(
  post: TagStoragePost | null | undefined
): boolean {
  return mediaFilesOf(post).some((file) => namedTagCount(file?.tags) > 0);
}

/** Whether the media hold the tags this post publishes, by its own rule. */
const mediaHoldTags = (post: TagStoragePost | null | undefined) =>
  isStorySet(post) ? hasPerStoryUserTags(post) : hasPerSlideTags(post);

/**
 * Every tag of one kind on the post, wherever it is currently stored. A tag with
 * no value is not counted (SKE-4713).
 */
export function countTags(
  post: TagStoragePost | null | undefined,
  key: TagArrayKey,
  perSlideTagging: boolean = true,
  perStoryTagging: boolean = false
): number {
  if (
    isPerSlideTagPost(post, perSlideTagging, perStoryTagging) &&
    mediaHoldTags(post)
  ) {
    return mediaFilesOf(post).reduce(
      (total, file) => total + namedTagCount(file?.[key]),
      0
    );
  }
  return namedTagCount(post?.[key]);
}

export type ReconciledTagStorage = {
  mediaFiles: TagStorageMediaFile[];
  tags: unknown[];
  productTags: unknown[];
};

/**
 * The post's tags moved into the shape this post's publish target reads.
 *
 * Returns null when nothing needs to move, so the caller writes to the form
 * only when there is something to write.
 *
 * A carousel collects the post-level arrays onto slide one, which is where they
 * publish today, and clears them. Both arrays move together even when only one
 * of them holds anything: the server's choice between the two shapes is made
 * once for the whole post, so leaving product tags behind while user tags moved
 * would stop the product tags being sent at all.
 *
 * Once a carousel's slides carry tags, the slides are the authority and any
 * post-level copy is stale and cleared. The server writes such a copy on
 * purpose for phone-track jobs (SKE-4666, `applyPhoneTrackCarouselTags` lifts
 * slide one back onto the post level, and defers to a non-empty post level so
 * an edit that only touches the post level still wins). Left in place here it
 * would be sent back verbatim after every edit made in the tagger, and the
 * server would keep publishing the original tags.
 *
 * SKE-4714. A story set tagged per story follows the same rules, except where
 * legacy post-level tags go. The publish path sends them on EVERY story of a
 * set in which no story carries its own, so they are copied onto every story
 * rather than onto the first, and the set publishes exactly as it did.
 *
 * Anything else collapses slide one's tags back to the post level, because a
 * single post (and each story of a set without per-story tagging) is built from
 * the post-level arrays and a slide array there would never be read. Tags on
 * slides past the first are dropped rather than merged: they were placed
 * against images this post no longer publishes as slides, and every story of a
 * set would otherwise be tagged with all of them.
 */
export function reconcileTagStorage(
  post: TagStoragePost | null | undefined,
  perSlideTagging: boolean = true,
  perStoryTagging: boolean = false
): ReconciledTagStorage | null {
  const mediaFiles = mediaFilesOf(post);
  if (mediaFiles.length === 0) return null;

  if (isPerSlideTagPost(post, perSlideTagging, perStoryTagging)) {
    const postLevelHoldsTags = TAG_ARRAY_KEYS.some(
      (key) => asArray(post?.[key]).length > 0
    );

    if (mediaHoldTags(post)) {
      if (!postLevelHoldsTags) return null;
      return { mediaFiles, tags: [], productTags: [] };
    }

    if (!postLevelHoldsTags) return null;

    const storySet = isStorySet(post);
    return {
      mediaFiles: mediaFiles.map((file, index) =>
        storySet || index === 0
          ? {
              ...file,
              tags: asArray(post?.tags),
              productTags: asArray(post?.productTags),
            }
          : file
      ),
      tags: [],
      productTags: [],
    };
  }

  if (!hasPerSlideTags(post)) return null;

  /*
   * SKE-4733 review. With the API publishing gate off a story set publishes
   * from the phone, which reads the post-level tags and ignores the media, so
   * nothing needs to move. Collapsing here would wipe the per-story tags an
   * enabled admin placed, the moment the tagger opened while the gate read off.
   */
  if (!perSlideTagging && isStorySet(post)) return null;

  const first = mediaFiles[0];
  const promote = (key: TagArrayKey) =>
    asArray(post?.[key]).length > 0
      ? asArray(post?.[key])
      : asArray(first[key]);

  return {
    mediaFiles: mediaFiles.map((file) => ({
      ...file,
      tags: [],
      productTags: [],
    })),
    tags: promote("tags"),
    productTags: promote("productTags"),
  };
}

/**
 * The slides the tagger offers.
 *
 * One per media file for a carousel, and exactly one for everything else, so
 * that it always lines up with `readSlideTags`. A story set gets one per story
 * when it is tagged per story (SKE-4714), and otherwise one slide, because its
 * tags are then the post's and publish on every story of the set.
 */
export function buildTagSlides(
  post: TagStoragePost | null | undefined,
  perSlideTagging: boolean = true,
  perStoryTagging: boolean = false
): TagSlide[] {
  const mediaFiles = mediaFilesOf(post);
  const noun = isStorySet(post) && perStoryTagging ? "Story" : "Slide";
  const first: TagSlide = {
    index: 0,
    stillUrl: resolveTagStillUrl(post ?? {}),
    isVideo: Boolean(mediaFiles[0]?.isVideo ?? post?.isVideo),
    label: `${noun} 1`,
  };
  if (!isPerSlideTagPost(post, perSlideTagging, perStoryTagging)) {
    return [{ ...first, label: "Slide 1" }];
  }

  return mediaFiles.map((file, index) =>
    index === 0
      ? first
      : {
          index,
          stillUrl: resolveMediaStillUrl(file, [file?.thumbnailUrl]),
          isVideo: Boolean(file?.isVideo),
          label: `${noun} ${index + 1}`,
        }
  );
}

/** The stored tags of one kind, in the shape `buildTagSlides` returns. */
export function readSlideTags(
  post: TagStoragePost | null | undefined,
  key: TagArrayKey,
  perSlideTagging: boolean = true,
  perStoryTagging: boolean = false
): unknown[][] {
  if (!isPerSlideTagPost(post, perSlideTagging, perStoryTagging)) {
    return [asArray(post?.[key])];
  }
  return mediaFilesOf(post).map((file) => asArray(file?.[key]));
}

/** The media files with one kind of tag replaced, slide for slide. */
export function writeSlideTags(
  post: TagStoragePost | null | undefined,
  key: TagArrayKey,
  slides: unknown[][]
): TagStorageMediaFile[] {
  return mediaFilesOf(post).map((file, index) => ({
    ...file,
    [key]: slides[index] ?? asArray(file?.[key]),
  }));
}
